import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import {
  VIOLATION_KINDS,
  formatViolationDate,
  formatViolationSummary,
  formatViolationDetails,
  formatViolationStage,
  getViolationRiderName
} from '../utils/violations';
import './ViolationsModal.css';

// Журнал нарушений: релегации и автофиксация контроля веса.
// Открывается двумя способами — по всему событию из карточки события и по одному
// участнику из списка участников (rider). Виден только админу и организаторам,
// доступ проверяет бэкенд (Api::ViolationsController).
const ViolationsModal = ({ event, rider, onClose }) => {
  const { t, language } = useLanguage();
  const [violations, setViolations] = useState([]);
  const [stageNumbers, setStageNumbers] = useState({});
  const [kind, setKind] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // t пересоздается на каждый рендер, а rider приходит объектом — в зависимостях
  // держим только идентификатор гонщика, иначе запрос уходит в цикл
  const riderId = rider ? rider.id : null;

  useEffect(() => {
    let cancelled = false;

    const fetchViolations = async () => {
      setLoading(true);
      setError('');

      try {
        const params = new URLSearchParams();
        if (riderId) params.append('user_id', riderId);
        if (kind) params.append('kind', kind);

        const response = await axios.get(`/api/events/${event.id}/violations?${params.toString()}`);
        if (cancelled) return;

        setViolations(response.data.violations || []);
        setStageNumbers(
          (response.data.stages || []).reduce((map, stage) => {
            map[stage.route_id] = stage.number;
            return map;
          }, {})
        );
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.error || 'load_error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchViolations();
    return () => { cancelled = true; };
  }, [event.id, riderId, kind]);

  const riderName = useMemo(
    () => (rider ? rider.name || getViolationRiderName(rider, language) : ''),
    [rider, language]
  );

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="violations-modal-overlay" onClick={handleOverlayClick}>
      <div className="violations-modal">
        <div className="violations-modal-header">
          <div>
            <h2>{t('violations.title')}</h2>
            <div className="violations-modal-subtitle">
              {rider ? `${event.name} — ${riderName}` : event.name}
            </div>
          </div>
          <button type="button" className="violations-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="violations-modal-filters">
          <button
            type="button"
            className={`violations-modal-filter ${kind === '' ? 'violations-modal-filter-active' : ''}`}
            onClick={() => setKind('')}
          >
            {t('violations.filter_all')}
          </button>
          {VIOLATION_KINDS.map(item => (
            <button
              key={item}
              type="button"
              className={`violations-modal-filter ${kind === item ? 'violations-modal-filter-active' : ''}`}
              onClick={() => setKind(item)}
            >
              {t(`violations.kind_${item}`)}
            </button>
          ))}
        </div>

        <div className="violations-modal-hint">{t('violations.automatic_hint')}</div>

        <div className="violations-modal-body">
          {loading ? (
            <div className="violations-modal-message">{t('common.loading')}</div>
          ) : error ? (
            <div className="violations-modal-error">
              {error === 'load_error' ? t('violations.load_error') : error}
            </div>
          ) : violations.length === 0 ? (
            <div className="violations-modal-message">{t('violations.empty')}</div>
          ) : (
            violations.map(violation => {
              const stage = formatViolationStage(violation, stageNumbers);
              const lines = formatViolationDetails(violation, t, language);

              return (
                <div key={violation.id} className={`violations-modal-item violations-modal-${violation.kind}`}>
                  <div className="violations-modal-item-head">
                    <span className={`violations-modal-badge violations-modal-badge-${violation.action}`}>
                      {t(`violations.action_${violation.action}`)}
                    </span>
                    <span className="violations-modal-kind">{t(`violations.kind_${violation.kind}`)}</span>
                    <span className="violations-modal-date">
                      {formatViolationDate(violation.occurred_at, language)}
                    </span>
                  </div>

                  {!rider && (
                    <div className="violations-modal-rider">
                      {getViolationRiderName(violation.user, language)}
                    </div>
                  )}

                  <div className="violations-modal-summary">
                    {formatViolationSummary(violation, t)}
                    {stage && <span className="violations-modal-stage"> · {stage}</span>}
                  </div>

                  {lines.map((line, index) => (
                    <div key={index} className="violations-modal-line">{line}</div>
                  ))}

                  <div className="violations-modal-author">
                    {violation.recorded_by
                      ? t('violations.recorded_by')
                          .replace('{name}', getViolationRiderName(violation.recorded_by, language))
                      : t('violations.recorded_automatically')}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ViolationsModal;
